import { useField, useStore } from '@tanstack/react-form'
import { DataPolicyColumnEffect } from '@vendia/management-api-types'
import clsx from 'clsx'
import debug from 'debug'
import { getValue } from 'src/utils/form/form'
import { getColumnOptions, getColumnPermissionOptions } from 'src/utils/lakehouse/flows'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'

import Button from '../buttons/button'
import ListboxField from '../fields/listbox.field'
import TextField from '../fields/text.field'
import { StepComponentProps, VendiaFlowFormApi } from '../flows/types'
import Alert from '../messages/alert'

const logger = debug('app:sharingPermissions')

export const EditColumnPolicies = ({
  context,
  policiesForm,
  fieldPath,
}: StepComponentProps<LakehouseFlowStepValues> & { policiesForm: VendiaFlowFormApi<any>; fieldPath?: string }) => {
  const selectedTableColumns = useStore(context.form.store, (state) => state.values.selectedTableColumns)
  const { columnPolicies, defaultColumnPolicyEffect } = useStore(policiesForm.store, (state) =>
    fieldPath ? getValue(state.values, fieldPath) : state.values,
  )

  const field = useField({
    name: fieldPath ? `${fieldPath}.columnPolicies` : 'columnPolicies',
    form: policiesForm,
    mode: 'array',
    defaultValue: columnPolicies,
  })

  const columnPermissionOptions = getColumnPermissionOptions(defaultColumnPolicyEffect)
  const snowflakeColumnOptions = getColumnOptions(selectedTableColumns, columnPolicies)

  async function addColumnPolicy(field: any) {
    await field.validate('change')
    if (field.state.meta.errors?.length === 0) {
      field.pushValue({ name: '', effect: '', value: '' })
    }
  }

  const clearColumnPolicy = (index: number) => {
    const policyAttributes = policiesForm.getFieldValue(`product.columnPolicies.${index}`)
    field.removeValue(index)
    // This is required because removeValue() does not remove the nested fields
    Object.keys(policyAttributes).forEach((attribute) =>
      policiesForm.deleteField(`product.columnPolicies.${index}.${attribute}`),
    )
  }

  logger('value from policiesForm', { columnPolicies, defaultColumnPolicyEffect })

  const isColumnIncludedInResults = (columnName: string) => {
    const explicitPolicy = columnPolicies?.find((policy: any) => policy.name === columnName)

    if (defaultColumnPolicyEffect === DataPolicyColumnEffect.Allow) {
      return !explicitPolicy || explicitPolicy.effect !== DataPolicyColumnEffect.Exclude
    } else {
      return explicitPolicy && explicitPolicy.effect === DataPolicyColumnEffect.Allow
    }
  }

  // Find TIME columns that will be included in results
  const timeColumnsIncluded =
    selectedTableColumns
      ?.filter((column) => {
        const columnType = column.type?.toLowerCase() || ''
        const isTimeType = columnType.includes('time') && !columnType.includes('timestamp')
        return isTimeType && isColumnIncludedInResults(column.name)
      })
      ?.map((column) => column.name) || []

  const timeWithTimezoneColumnsIncluded =
    selectedTableColumns
      ?.filter((column) => {
        const columnType = column.type?.toLowerCase() || ''
        const isTimeWithTimezoneType = columnType.includes('time with time zone')
        return isTimeWithTimezoneType && isColumnIncludedInResults(column.name)
      })
      ?.map((column) => column.name) || []

  return (
    <div className='flex w-full flex-1 flex-col pt-2'>
      {timeColumnsIncluded.length > 0 && (
        <Alert kind='info' className='mb-4'>
          TIME columns ({timeColumnsIncluded.join(', ')}) will be converted to INT during import, formatted as
          milliseconds after midnight.
        </Alert>
      )}
      {timeWithTimezoneColumnsIncluded.length > 0 && (
        <Alert kind='info' className='mb-4'>
          TIME WITH TIME ZONE columns ({timeWithTimezoneColumnsIncluded.join(', ')}) are not supported by Vendia and
          will be omitted during import.
        </Alert>
      )}
      {columnPolicies?.length === 0 && (
        <div className='text-light mb-6 mt-2 text-sm'>
          {defaultColumnPolicyEffect === DataPolicyColumnEffect.Exclude ? (
            <div>No columns are included by default. Add columns by configuring rules.</div>
          ) : (
            <div>All columns are included by default. Exclude columns by configuring rules.</div>
          )}
        </div>
      )}
      {columnPolicies?.length > 0 && (
        <div className={clsx('mb-4 flex h-5/6 w-full max-w-7xl flex-col justify-center gap-3')}>
          {columnPolicies?.map((policy: any, index: number) => {
            const fieldPrefix = fieldPath ? `${fieldPath}.columnPolicies.${index}` : `columnPolicies.${index}`
            logger('column policy', { fieldPrefix, index, policy })

            return (
              <div
                key={index}
                className={'rounded-md border border-slate-300 bg-slate-50 p-4'}
                data-testid={`column-sharing-policy-${index}`}
              >
                <div className='grid grid-cols-[auto,2fr,auto,1fr,1fr,auto] items-center justify-stretch gap-4'>
                  <span className='text-light self-center'>When</span>
                  <ListboxField
                    name={`${fieldPrefix}.name`}
                    label='Attribute'
                    options={snowflakeColumnOptions}
                    data-testid={`column-policy-attribute-select-${index}`}
                    validators={{
                      onChange: ({ value }) => (!value ? 'Attribute is required' : undefined),
                    }}
                    useNestedLabel
                    wrapperClassName='!mb-0'
                    form={policiesForm}
                  />
                  <span className='text-light self-center whitespace-nowrap'>is present,</span>
                  <ListboxField
                    name={`${fieldPrefix}.effect`}
                    label='Action'
                    options={columnPermissionOptions}
                    data-testid={`column-policy-action-select-${index}`}
                    wrapperClassName='!mb-0'
                    validators={{
                      onChange: ({ value }) => (!value ? 'Action is required' : undefined),
                    }}
                    useNestedLabel
                    form={policiesForm}
                  />
                  <div>
                    <policiesForm.Subscribe selector={(state) => getValue(state.values, fieldPrefix)}>
                      {(policy) => {
                        if (
                          [DataPolicyColumnEffect.MaskExceptLast, DataPolicyColumnEffect.Replace].includes(
                            policy?.effect,
                          )
                        ) {
                          return (
                            <>
                              <TextField
                                name={`${fieldPrefix}.value`}
                                label={
                                  policy.effect === DataPolicyColumnEffect.MaskExceptLast ? 'Constraint' : 'Replacement'
                                }
                                placeholder='Enter replacement value'
                                data-testid={`column-policy-value-input-${index}`}
                                className='min-w-20'
                                wrapperClassName='!mb-0 '
                                validators={{
                                  onChange: ({ value }) => {
                                    if (!value || value.trim() === '') {
                                      return policy.effect === DataPolicyColumnEffect.MaskExceptLast
                                        ? 'Constraint is required'
                                        : 'Replacement is required'
                                    }
                                    if (
                                      policy.effect === DataPolicyColumnEffect.MaskExceptLast &&
                                      Number.isNaN(parseInt(value))
                                    ) {
                                      return 'Must be a number'
                                    }
                                  },
                                }}
                                useNestedLabel
                                form={policiesForm}
                                rightOfInput={
                                  policy.effect === DataPolicyColumnEffect.MaskExceptLast ? (
                                    <span className='text-light ml-4 self-center'>chars</span>
                                  ) : (
                                    <></>
                                  )
                                }
                              />
                            </>
                          )
                        }
                      }}
                    </policiesForm.Subscribe>
                  </div>
                  <div className={'py-2'}>
                    <Button kind='icon' icon='trash' onClick={() => clearColumnPolicy(index)} />
                  </div>
                </div>
                <policiesForm.Subscribe selector={(state) => getValue(state.values, fieldPrefix)}>
                  {(policy) => {
                    // Check if there's a type change (non-string type with masking/replace policy)
                    const selectedColumn = selectedTableColumns.find((col) => col.name === policy?.name)
                    const columnType = selectedColumn?.type?.toLowerCase() || ''
                    const isStringType =
                      columnType.includes('string') || columnType.includes('varchar') || columnType.includes('text')
                    const hasMaskingOrReplace = [
                      DataPolicyColumnEffect.Mask,
                      DataPolicyColumnEffect.MaskExceptLast,
                      DataPolicyColumnEffect.Replace,
                    ].includes(policy?.effect)
                    const hasTypeChange = !isStringType && hasMaskingOrReplace

                    return (
                      <>
                        {field.state.meta.errors?.length > 0 && (
                          <Alert className='mt-2'>{field.state.meta.errors[0]}</Alert>
                        )}
                        {field.state.meta.errors?.length === 0 && hasTypeChange && (
                          <Alert kind='info' className='mt-2'>
                            This column will be converted to type STRING with the selected masking/replacement rules.
                          </Alert>
                        )}
                      </>
                    )
                  }}
                </policiesForm.Subscribe>
              </div>
            )
          })}
        </div>
      )}
      <div className='flex w-full max-w-4xl pt-2'>
        <Button
          kind={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            addColumnPolicy(field)
          }}
          icon='plus-m'
          iconSize={14}
        >
          {field.state.value.length ? 'Add another' : 'Add rule'}
        </Button>
      </div>
    </div>
  )
}
